
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    createLeaveApplication,
    getAllEmployees,
    getAllLeaveTypesForApplication,
    getAllSupervisors,
    getLeaveStats
} from 'adminApi/LeaveApi/LeaveApplicationApi';
import { validateForm } from "utils/validation";
import { LeaveApplicationRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import AddLeaveApplicationForm from "./Components/AddLeaveApplicationForm";
import fields from "../json/LeaveApplicationFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";

const AddLeaveApplication = () => {
    const navigate = useNavigate();
    const { formdata } = fields;
    const [options, setOptions] = useState({ employee: [], leave_type: [], supervisor: [] });
    const [leaveStats, setLeaveStats] = useState({ enjoyed_days: 0, total_days: 0 });

    // Initialize form values
    const initialValues = formdata.reduce((acc, field) => ({ ...acc, [field.name]: field.name === "day_type" ? 1 : "" }), {});

    const { values, errors, handleChange, handleFileChange, setValues, setErrors } = useFormChange(
        initialValues,
        LeaveApplicationRules
    );

    // Fetch initial options
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const currentYear = new Date().getFullYear();
                const [empRes, typeRes, supRes] = await Promise.all([
                    getAllEmployees(),
                    getAllLeaveTypesForApplication(currentYear),
                    getAllSupervisors()
                ]);

                setOptions({
                    employee: empRes.success ? empRes.data.map(e => ({ id: e.employee_id, name: e.employee_name })) : [],
                    leave_type: typeRes.success ? typeRes.data.map(t => ({ id: t.leave_type_id, name: t.leave_type })) : [],
                    supervisor: supRes.success ? supRes.data.map(s => ({ id: s.employee_id, name: s.supervisor_name })) : []
                });
            } catch (err) {
                console.error("Error fetching options", err);
            }
        };
        fetchOptions();
    }, []);

    // Auto-calculate applied_days
    useEffect(() => {
        if (values.applied_start_date && values.applied_end_date) {
            const start = new Date(values.applied_start_date);
            const end = new Date(values.applied_end_date);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0);
                const diffTime = Math.abs(end - start);
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
                const multiplier = Number(values.day_type) === 0 ? 0.5 : 1;
                setValues(prev => ({ ...prev, applied_days: Math.max(diffDays * multiplier, 0) }));
            }
        }
    }, [values.applied_start_date, values.applied_end_date, values.day_type, setValues]);

    // Auto-calculate approved_days
    useEffect(() => {
        if (values.approved_start_date && values.approved_end_date) {
            const start = new Date(values.approved_start_date);
            const end = new Date(values.approved_end_date);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0);
                const diffTime = Math.abs(end - start);
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
                const multiplier = Number(values.day_type) === 0 ? 0.5 : 1;
                setValues(prev => ({ ...prev, approved_days: Math.max(diffDays * multiplier, 0) }));
            }
        }
    }, [values.approved_start_date, values.approved_end_date, values.day_type, setValues]);

    // Fetch leave stats when employee_id or leave_type_id changes
    useEffect(() => {
        if (values.employee_id && values.leave_type_id) {
            const fetchStats = async () => {
                const res = await getLeaveStats({ employee_id: values.employee_id, leave_type_id: values.leave_type_id });
                if (res.success) {
                    setLeaveStats(res.data);
                }
            };
            fetchStats();
        } else {
            setLeaveStats({ enjoyed_days: 0, total_days: 0 });
        }
    }, [values.employee_id, values.leave_type_id]);

    // Form submit handler
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => {
                formData.append(key, val ?? "");
            });
            return await createLeaveApplication({ data: formData });
        },
        (vals) => {
            const err = validateForm(vals, LeaveApplicationRules)
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Leave Application submitted successfully"
    );

    return (
        <>
            {loadingSubmit && <Spinner isSpinner={true} />}
            <AddLeaveApplicationForm
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                options={options}
                imagePreview={values.attachmentPreview || values.attachment}
                leaveStats={leaveStats}
            />
        </>
    );
};

export default AddLeaveApplication;
